import { apiFetch, API_BASE_URL } from "./client";

/* =========================
   AUTH API
========================= */

/* ----- OAuth (Google / Microsoft) ----- */

/**
 * Backend-Start-URL für den OAuth-Flow (Top-Level-Navigation dorthin).
 */
export function oauthStartUrl(provider) {
  return `${API_BASE_URL}/api/v1/auth/oauth/${provider}/start`;
}

/**
 * Liefert, welche OAuth-Provider aktiviert sind (abhängig von Backend-Config).
 */
export async function getOAuthProviders() {
  return apiFetch("/auth/oauth/providers");
}

/**
 * Tauscht den Einmal-Code aus dem OAuth-Callback gegen ein Access-Token.
 * Setzt dabei den Refresh-Cookie im korrekten (Frontend-)Origin-Kontext.
 */
export async function oauthExchange(code) {
  return apiFetch("/auth/oauth/exchange", {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

/**
 * Mintet einen Einmal-Code fuer die aktuell angemeldete Web-Session, den die
 * native App gegen eigene Tokens eintauscht (Web-Login-Handoff).
 */
export async function mintMobileAuthCode() {
  return apiFetch("/auth/oauth/mobile-code", {
    method: "POST",
  });
}

/**
 * Registrierung
 */
export async function register(firstName, lastName, email, password, preferredLanguage = "de") {
  return apiFetch("/auth/register", {
    method: "POST",
    body: JSON.stringify({ firstName, lastName, email, password, preferredLanguage }),
  });
}

/**
 * Login
 * → liefert { accessToken }
 * → Errors enthalten .code (z.B. EMAIL_NOT_VERIFIED, INVALID_CREDENTIALS, ACCOUNT_LOCKED)
 */
export async function login(email, password) {
  return apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/**
 * Aktuellen Benutzer abrufen
 */
export async function getMe() {
  return apiFetch("/auth/me");
}

/**
 * Refresh Access Token
 * → nutzt httpOnly Cookie
 */
export async function refresh() {
  return apiFetch("/auth/refresh", {
    method: "POST",
  });
}

/**
 * Logout
 * → invalidiert Refresh Token
 */
export async function logout() {
  return apiFetch("/auth/logout", {
    method: "POST",
  });
}

/**
 * Bestätigungs-Mail erneut senden
 * → IMMER gleiche Response (Anti-Enumeration)
 */
export async function resendVerification(email) {
  return apiFetch("/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/**
 * Passwort zurücksetzen anfordern
 * → sendet Reset-Link per E-Mail
 */
export async function forgotPassword(email) {
  return apiFetch("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/**
 * Passwort zurücksetzen mit Token
 * → setzt neues Passwort
 */
export async function resetPassword(token, password) {
  return apiFetch("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

/**
 * Passwort im eingeloggten Zustand ändern
 * → benötigt aktuelles Passwort
 */
export async function changePassword(currentPassword, newPassword) {
  return apiFetch("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/**
 * Account löschen
 * → löscht den aktuellen Benutzer aus der Datenbank
 */
export async function deleteAccount() {
  return apiFetch("/auth/delete-account", {
    method: "DELETE",
  });
}

/**
 * Profil aktualisieren
 */
export async function updateProfile(firstName, lastName, favoriteCompanyId, preferredLanguage) {
  const body = { firstName, lastName, favoriteCompanyId };
  if (preferredLanguage !== undefined) {
    body.preferredLanguage = preferredLanguage;
  }
  return apiFetch("/auth/update-profile", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Profilbild + Titel aktualisieren (nur Superadmins).
 *
 * Cloud Armor blockiert vollstaendige "data:"-URLs im Body, deshalb wird das
 * Bild in Base64 + MIME-Type aufgeteilt uebertragen (wie beim Favicon).
 *
 * @param {Object} updates
 * @param {string|null|undefined} updates.avatarDataUrl - Data-URL des Bildes,
 *   null zum Loeschen, undefined = nicht aendern.
 * @param {string|null|undefined} updates.title - Titel (max. 30 Zeichen),
 *   null/"" zum Loeschen, undefined = nicht aendern.
 */
export async function updateProfileMedia({ avatarDataUrl, title } = {}) {
  const body = {};

  if (avatarDataUrl !== undefined) {
    if (avatarDataUrl === null || avatarDataUrl === "") {
      body.avatarBase64 = null;
    } else if (typeof avatarDataUrl === "string") {
      const match = avatarDataUrl.match(
        /^data:(image\/[a-zA-Z0-9+.-]+);base64,(.+)$/
      );
      if (match) {
        body.avatarMimeType = match[1];
        body.avatarBase64 = match[2];
      } else {
        body.avatarBase64 = avatarDataUrl;
      }
    }
  }

  if (title !== undefined) {
    body.title = title;
  }

  return apiFetch("/auth/update-profile-media", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * Profilbild eines beliebigen Nutzers setzen/entfernen (nur Superadmins).
 * @param {number|string} userId
 * @param {string|null} avatarDataUrl - Data-URL oder null zum Entfernen.
 */
export async function updateUserAvatar(userId, avatarDataUrl) {
  const body = {};
  if (avatarDataUrl === null || avatarDataUrl === "") {
    body.avatarBase64 = null;
  } else if (typeof avatarDataUrl === "string") {
    const match = avatarDataUrl.match(
      /^data:(image\/[a-zA-Z0-9+.-]+);base64,(.+)$/
    );
    if (match) {
      body.avatarMimeType = match[1];
      body.avatarBase64 = match[2];
    } else {
      body.avatarBase64 = avatarDataUrl;
    }
  }
  return apiFetch(`/auth/users/${userId}/avatar`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}